import type { Tutorial } from "@/config/tutorials.generated";

import {
  getGroupSections,
  getMainSidebarNodes,
} from "@/components/sidebar/main-sidebar-nodes";
import { ORGANIZATION_TYPE } from "@/types/organization";
import { TUTORIALS } from "@/config/tutorials.generated";
import { withUtmCampaign } from "@/utils/url";
import { tutorialGroupSlug } from "@/config/tutorials";
export { tutorialGroupSlug, tutorialAreaPath } from "@/config/tutorials";

export type { Tutorial } from "@/config/tutorials.generated";

export const ARCADE_EMBED_ASPECT = 917 / 1512;
export const ARCADE_EMBED_CONTROL_BAR = 41;

export const embedUrl = (tutorial: Tutorial): string => tutorial.embedUrl;

const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const pathSegments = (pathname: string): string[] =>
  pathname
    .split(/[?#]/)[0]
    .split("/")
    .filter(Boolean)
    .map((segment) => (UUID_SEGMENT.test(segment) ? ":id" : segment));

export const findTutorialById = (id: number): Tutorial | undefined =>
  TUTORIALS.find((tutorial) => tutorial.id === id);

export const findNextTutorial = (tutorial: Tutorial): Tutorial | undefined =>
  TUTORIALS.find((entry) => entry.id === tutorial.nextId);

export const findTutorialsByGroup = (group: string): Tutorial[] =>
  TUTORIALS.filter((tutorial) => tutorial.group === group);

export const tutorialLink = (tutorial: Tutorial): string =>
  withUtmCampaign(tutorial.shareUrl);

const placementLabels = new Map(
  getMainSidebarNodes(ORGANIZATION_TYPE.CLINIC).flatMap<[string, string]>(
    (node) => {
      if (node.kind === "divider") return [];
      if (node.kind === "item") return [[node.href, node.title]];

      return [
        [node.href, node.title],
        ...getGroupSections(node).flatMap((section) =>
          section.children.map<[string, string]>((child) => {
            const title =
              child.title === "General" ? section.label : child.title;
            const parent =
              section.label === "Workspace" && child.title === "Members"
                ? `${node.title} › ${section.label}`
                : node.title;

            return [child.href, `${parent} › ${title}`];
          }),
        ),
      ];
    },
  ),
);

export function placementLabel(route: string): string {
  const segments = route.split(/[?#]/)[0].split("/").filter(Boolean);

  while (segments.at(-1) === ":id" || UUID_SEGMENT.test(segments.at(-1) ?? ""))
    segments.pop();
  const path = `/${segments.join("/")}`;
  const exact = placementLabels.get(path);

  if (exact) return exact;
  const last = segments.pop();
  const title = (last ?? "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
  const parent = placementLabels.get(`/${segments.join("/")}`);

  return parent ? `${parent} › ${title}` : title;
}

export function tutorialMeta(tutorial: Tutorial): string {
  const { chapters, steps, seconds, placements } = tutorial;
  const placement = placements[0] ? placementLabel(placements[0]) : "";

  return [
    `${chapters.length} ${chapters.length === 1 ? "chapter" : "chapters"}`,
    `${steps} ${steps === 1 ? "step" : "steps"}`,
    `${Math.max(1, Math.ceil(seconds / 60))} min`,
    placement,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function groupTutorialsByFlow(
  tutorials: readonly Tutorial[],
): Tutorial[][] {
  const flows = new Map<string, Tutorial[]>();

  for (const tutorial of tutorials) {
    const topics = flows.get(tutorial.flowId);

    if (topics) topics.push(tutorial);
    else flows.set(tutorial.flowId, [tutorial]);
  }

  return [...flows.values()];
}

export function tutorialPagePath(
  tutorial: Tutorial,
  workspaceId: string | null,
): string | undefined {
  const placement = tutorial.placements.find((path) => path.startsWith("/"));

  if (!placement) return undefined;
  const segments = pathSegments(placement);
  const idIndex = segments.indexOf(":id");

  if (idIndex === -1) return placement;
  if (!workspaceId) return `/${segments.slice(0, idIndex).join("/")}`;

  return `/${segments.map((segment) => (segment === ":id" ? workspaceId : segment)).join("/")}`;
}

export function tutorialSections(
  tutorials: readonly Tutorial[],
  search: string,
) {
  const query = search.trim().toLowerCase();
  const flows = groupTutorialsByFlow(tutorials);

  return [...new Set(flows.map(([tutorial]) => tutorial.category))]
    .map((category) => ({
      id: tutorialGroupSlug(category),
      label: category,
      tutorials: flows.filter(
        (topics) =>
          topics[0].category === category &&
          topics.some((tutorial) =>
            [
              tutorial.title,
              tutorial.category,
              ...tutorial.tags,
              ...tutorial.chapters.flatMap(({ title, subtitle }) => [
                title,
                subtitle,
              ]),
            ].some((text) => text.toLowerCase().includes(query)),
          ),
      ),
    }))
    .filter((section) => section.tutorials.length > 0);
}
